import { useLocation } from "react-router-dom";

import { APP_TYPE, TiVoLaunchAttributes } from "hooks/tivoApps";
import {
  TIVO_LAUNCH_TYPES,
  TiVoAppProvider,
  TiVoPresentation,
  TiVoAppVariantVersion,
  TiVoLaunchType,
  TiVoPresentationAttributes,
  TiVoPresentationConfig,
  TiVoUriTransform,
  ZoomConfiguration,
  TiVoTargets,
} from "hooks/tivoApps/types";
import { Country } from "utils/continents/utils";
import { getInitialDate, prepareDateToRequest } from "utils/date";
import { deepMerge } from "utils/object";

import { PRESENTATION_BOOLEAN, IMAGE_ENVS } from "../constants";
import { getPresentationBooleanEntries, toJsonString } from "../utils";
import { TEXTS } from "./constants";
import { VARIANT_FIELDS, PROVIDER_FIELDS } from "./fields";
import {
  FormMode,
  ProviderForm,
  TiVoPresentationAttributesForm,
  TiVoPresentationConfigForm,
  TiVoPresentationForm,
  VariantForm,
} from "./types";

// - - - - - - - - Initial Field values

function getCountriesInitialValues(
  data: TiVoAppProvider | TiVoAppVariantVersion | undefined,
  countryCount: number | undefined,
  canInherit?: boolean
): VariantForm["worldwide"] {
  let worldwide = canInherit ? "unset" : "true";
  if (data?.countries.length && countryCount) {
    worldwide = data.countries.length >= countryCount ? "true" : "false";
  }
  return worldwide as VariantForm["worldwide"];
}

function getLaunchAttributesInitialValues(
  variantVersionData?: TiVoAppVariantVersion
): VariantForm["launchAttributes"] {
  return TIVO_LAUNCH_TYPES.reduce<VariantForm["launchAttributes"]>(
    (acc, launchType) => {
      const attrs = variantVersionData?.launchAttributes[launchType];
      if (!attrs) {
        return acc;
      }

      const filteredAttrs = Object.fromEntries(
        Object.entries(attrs).filter(([, value]) => value !== null)
      );

      return {
        ...acc,
        [launchType]: {
          ...getLaunchAttributes(filteredAttrs),
          validationMode: filteredAttrs.intent ? "intent" : "uri",
        },
      };
    },
    { appType: variantVersionData?.launchAttributes.appType || "" }
  );
}

function getLaunchAttributesTabsInitialValues(
  variantVersionData?: TiVoAppVariantVersion
): VariantForm["launchAttributesTabs"] {
  return TIVO_LAUNCH_TYPES.reduce<VariantForm["launchAttributesTabs"]>(
    (acc, launchType) => {
      const attrs = variantVersionData?.launchAttributes[launchType];
      if (!attrs) {
        return acc;
      }
      return [
        ...acc,
        {
          launchMode: attrs.intent ? "intent" : "uri",
          launchType,
        },
      ];
    },
    []
  );
}

// Normalize API presentation config before merge: drop null/undefined attributes &
// convert only present boolean attributes to form strings

function normalizeApiPresentationConfig(
  config: TiVoPresentationConfig
): Partial<TiVoPresentationConfig> {
  const booleanAttrs = getPresentationBooleanEntries(config.attributes).reduce<
    Record<string, unknown>
  >((acc, { key, value }) => {
    acc[key] = value ? "on" : "off";
    return acc;
  }, {});

  const filtered = filterEmptyValues(config) || {};

  return {
    ...filtered,
    attributes: {
      ...filtered.attributes,
      ...booleanAttrs,
    } as Partial<TiVoPresentationAttributes>,
  };
}

function mapPresentationConfig(
  config: TiVoPresentationConfig
): TiVoPresentationConfigForm {
  const useDefaults =
    !config.countries.length && !config.platformSupportedFeatures?.length;

  const normalizedConfig = normalizeApiPresentationConfig(config);

  const configForm = deepMerge<TiVoPresentationConfigForm>(
    getInitialPresentationConfig(useDefaults),
    normalizedConfig
  );

  if (!config.countries.length && config.platformSupportedFeatures?.length) {
    configForm.matchByPlatformSupportedFeatures = true;
  }

  return configForm;
}

function getPresentationsInitialValues(
  presentations: TiVoPresentation[] = []
): ProviderForm["presentations"] {
  if (!presentations.length) {
    return [];
  }

  const adaptedPresentations: ProviderForm["presentations"] = [];
  for (const presentation of presentations) {
    const presentationConfigs = presentation.configs.map((config) => {
      const configForm = mapPresentationConfig(config);
      configForm.attributes.postPublishDaysConsideredNew =
        config.attributes.postPublishDaysConsideredNew ?? "";
      configForm.attributes.publishDateTime = getInitialDate(
        config.attributes.publishDateTime
      );
      return configForm;
    });

    adaptedPresentations.push({
      releaseStage: presentation.releaseStage,
      configs:
        presentationConfigs.length > 0
          ? presentationConfigs
          : [getInitialPresentationConfig(true)],
      label: presentation.label || "",
      targets: presentation.targets || {},
      partnerPicks: {
        aspectRatio: presentation.partnerPicks?.aspectRatio || "",
        reporting: presentation.partnerPicks?.reporting || "",
        type: presentation.partnerPicks?.type || "",
      },
    });
  }

  return adaptedPresentations;
}

function getUriTransformInitialValues(
  variantVersionData?: TiVoAppVariantVersion
): VariantForm["uriTransform"] {
  const providerSpecificNameValueMap = Object.entries(
    variantVersionData?.uriTransform?.providerSpecificNameValueMap || {}
  ).map(([name, value]) => ({
    name,
    value,
  }));
  return {
    enabled: false,
    ...(variantVersionData?.uriTransform || {}),
    alternateBasePlaybackUrl:
      variantVersionData?.uriTransform?.alternateBasePlaybackUrl || "",
    className: variantVersionData?.uriTransform?.className || "",
    providerSpecificNameValueMap,
  };
}

function getZoomConfigInitialValues(
  zoomConfigurations?: ZoomConfiguration[]
): VariantForm["zoomConfigurations"] {
  return (
    zoomConfigurations?.map(({ targets, zoomFactor, zoomFactorRatio }) => ({
      targets,
      zoomFactor: zoomFactor ?? "",
      zoomFactorRatio: zoomFactorRatio ?? "",
    })) || []
  );
}
// - - - - - - - - Initial Form values

export function getProviderFormInitialValues(
  providerData?: TiVoAppProvider,
  countriesData?: Country[]
): ProviderForm {
  return {
    // Non-submittable fields
    mode: "provider" as const,
    worldwide: getCountriesInitialValues(providerData, countriesData?.length),

    providerId: providerData?.providerId ?? "",
    universalProviderId: providerData?.universalProviderId ?? "",
    jwProviderId: providerData?.jwProviderId ?? [],
    appCategories: providerData?.appCategories ?? [],
    allowList: providerData?.allowList ?? [],
    label: providerData?.label ?? "",
    displayName: providerData?.displayName ?? "",
    cspConfirmationRequired: providerData?.cspConfirmationRequired ?? "unknown",
    assets: providerData?.assets ?? {},
    supportAdditionalContacts: providerData?.supportAdditionalContacts ?? "",
    supportEmail: providerData?.supportEmail ?? "",
    supportNotes: providerData?.supportNotes ?? "",
    supportPhone: providerData?.supportPhone ?? "",
    supportPriority: providerData?.supportPriority ?? "Tier 2",
    supportSla: providerData?.supportSla ?? "",
    assignedBdm: providerData?.assignedBdm ?? "",
    assignedTam: providerData?.assignedTam ?? "",
    acrRights: providerData?.acrRights ?? undefined,
    placementObligations: providerData?.placementObligations ?? "",
    promotionsObligations: providerData?.promotionsObligations ?? "",
    reportingObligations: providerData?.reportingObligations ?? "",
    cspBusinessModel: providerData?.cspBusinessModel ?? [],
    commercialAgreementType: providerData?.commercialAgreementType ?? [],
    commercialAgreementSummary: providerData?.commercialAgreementSummary ?? "",
    cspGroup: providerData?.cspGroup ?? undefined,
    dealSummary: providerData?.dealSummary ?? "",
    countries: providerData?.countries ?? [],
    contractStatus: providerData?.contractStatus ?? undefined,
    contractOrigin: providerData?.contractOrigin ?? "Xperi",
    presentations: getPresentationsInitialValues(providerData?.presentations),
  };
}

export function getVariantFormInitialValues(
  providerData?: TiVoAppProvider,
  variantVersionData?: TiVoAppVariantVersion,
  countriesData?: Country[]
): VariantForm {
  const adsEnabled: VariantForm["adsEnabled"] = variantVersionData?.adsEnabled
    ? "true"
    : "false";
  const nonSubmittableFields = {
    launchAttributesTabs:
      getLaunchAttributesTabsInitialValues(variantVersionData),
    mode: "variant" as const,
    worldwide: getCountriesInitialValues(
      variantVersionData,
      countriesData?.length,
      true
    ),
    inheritedCountries: providerData?.countries,
  };

  const baseValues: Omit<VariantForm, "launchAttributes" | "uriTransform"> = {
    ...nonSubmittableFields,
    adsEnabled,
    countries: variantVersionData?.countries || [],
    label: variantVersionData?.label || "",
    targets: variantVersionData?.targets || {},
    zoomConfigurations: getZoomConfigInitialValues(
      variantVersionData?.zoomConfigurations
    ),
  };

  return {
    ...baseValues,
    launchAttributes: getLaunchAttributesInitialValues(variantVersionData),
    uriTransform: getUriTransformInitialValues(variantVersionData),
  };
}

// - - - - - - - - Other fields setup

export function getLaunchAttributes(
  attrs?: TiVoLaunchAttributes
): TiVoLaunchAttributes {
  return {
    adParamsTemplate: "",
    appendUrlParameters: [],
    playbackUri: "",
    ...attrs,
    attributesWithCriteria: toJsonString(attrs?.attributesWithCriteria),
    intent: attrs?.intent ? toJsonString(attrs.intent) : "",
  };
}

export function getLaunchConfigTabField(launchType: TiVoLaunchType | "") {
  let field: { label: string; name?: string } = {
    label: TEXTS.LAUNCH_CONFIG.TAB_NEW_LABEL,
  };
  switch (launchType) {
    case "launch":
      field = { ...field, ...VARIANT_FIELDS.LAUNCH_CONFIG.LAUNCH_TAB };
      break;
    case "linear":
      field = { ...field, ...VARIANT_FIELDS.LAUNCH_CONFIG.LINEAR_TAB };
      break;
    case "searchLaunch":
      field = { ...field, ...VARIANT_FIELDS.LAUNCH_CONFIG.SEARCH_LAUNCH_TAB };
      break;
    case "seriesBrowse":
      field = { ...field, ...VARIANT_FIELDS.LAUNCH_CONFIG.SERIES_BROWSE_TAB };
      break;
    case "vod":
      field = { ...field, ...VARIANT_FIELDS.LAUNCH_CONFIG.VOD_TAB };
      break;
  }
  return field;
}

// - - - - - - - - Submit form values

export function adaptPresentationToRequest(
  presentation: TiVoPresentationForm
): {
  type: "string";
  attributes: TiVoPresentation;
} {
  const mapPresentationBoolean = (value: keyof typeof PRESENTATION_BOOLEAN) => {
    switch (value) {
      case "on":
        return true;
      case "off":
        return false;
      default:
        return null;
    }
  };
  const mapPresentationAttributes = (
    attributes: TiVoPresentationAttributesForm
  ): TiVoPresentationAttributes => ({
    alwaysVisibleInMyApps: mapPresentationBoolean(
      attributes.alwaysVisibleInMyApps
    ),
    appManifestAvailable: mapPresentationBoolean(
      attributes.appManifestAvailable
    ),
    browsePlaybackSortOrder: attributes.browsePlaybackSortOrder || null,
    enabledByDefault: mapPresentationBoolean(attributes.enabledByDefault),
    inMyAppsByDefault: mapPresentationBoolean(attributes.inMyAppsByDefault),
    myAppsRangeGroup: attributes.myAppsRangeGroup || null,
    name: attributes.name || null,
    platformSupportedApps:
      attributes.platformSupportedApps &&
      attributes.platformSupportedApps.length > 0
        ? attributes.platformSupportedApps
        : null,
    promoted: mapPresentationBoolean(attributes.promoted),
    recommendationsType:
      attributes.recommendationsType === "inherited"
        ? null
        : attributes.recommendationsType || null,
    shouldShowInList: mapPresentationBoolean(attributes.shouldShowInList),
    shouldShowInSearchInApps: mapPresentationBoolean(
      attributes.shouldShowInSearchInApps
    ),
    sortOrder: attributes.sortOrder || null,
    supportsEpisodeDeepLinks: mapPresentationBoolean(
      attributes.supportsEpisodeDeepLinks
    ),
    postPublishDaysConsideredNew:
      attributes.postPublishDaysConsideredNew === ""
        ? null
        : attributes.postPublishDaysConsideredNew,
    publishDateTime: prepareDateToRequest(attributes.publishDateTime) || null,
  });
  const mapPresentationConfig = (
    config: TiVoPresentationConfigForm
  ): TiVoPresentationConfig => {
    const { matchByPlatformSupportedFeatures, ...submittableConfig } = config;

    return {
      ...submittableConfig,
      attributes: mapPresentationAttributes(submittableConfig.attributes),
      countries: !matchByPlatformSupportedFeatures
        ? submittableConfig.countries || []
        : [],
      platformSupportedFeatures: matchByPlatformSupportedFeatures
        ? submittableConfig.platformSupportedFeatures
        : [],
    };
  };

  const stripClientTypes = (targets: TiVoTargets) => {
    const { clientTypes, ...rest } = targets;
    return rest;
  };

  return {
    type: "string",
    attributes: {
      configs: presentation.configs.map(mapPresentationConfig),
      label: presentation.label,
      releaseStage: presentation.releaseStage,
      targets: stripClientTypes(presentation.targets),
      partnerPicks: {
        aspectRatio: presentation.partnerPicks.aspectRatio || null,
        reporting: presentation.partnerPicks.reporting || null,
        type: presentation.partnerPicks.type || null,
      },
    },
  };
}

function adaptSupportedCountries(
  form: ProviderForm | VariantForm,
  countries?: Country[]
): string[] | undefined {
  if (form.worldwide === "unset") {
    return undefined;
  }

  if (form.worldwide === "true") {
    return countries?.map(({ code }) => code) || [];
  }

  return form.countries?.length ? [...form.countries] : [];
}

export function adaptProviderFormToRequest(
  form: ProviderForm,
  countries?: Country[]
): Partial<TiVoAppProvider> {
  // destructure fields we don't want to submit
  const { id, mode, worldwide, presentations, ...data } = form;
  return {
    ...(filterEmptyValues(data) || {}),
    countries: adaptSupportedCountries(form, countries),
  };
}

function adaptLaunchAttributes(
  launchAttributes: VariantForm["launchAttributes"],
  launchAttributesTabs: VariantForm["launchAttributesTabs"]
): TiVoAppVariantVersion["launchAttributes"] {
  const adapted: TiVoAppVariantVersion["launchAttributes"] = {
    appType: launchAttributes.appType as keyof typeof APP_TYPE,
  };
  launchAttributesTabs.forEach(({ launchMode, launchType }) => {
    if (!launchMode || !launchType || !launchAttributes[launchType]) {
      return;
    }
    const { attributesWithCriteria, intent, validationMode, ...uriParams } =
      launchAttributes[launchType];
    if (launchMode === "intent" && intent) {
      let intentConfig: TiVoLaunchAttributes = { intent: JSON.parse(intent) };
      if (attributesWithCriteria) {
        intentConfig = {
          ...intentConfig,
          attributesWithCriteria: JSON.parse(attributesWithCriteria),
        };
      }
      adapted[launchType] = intentConfig;
      return;
    }
    if (launchMode === "uri") {
      let uriConfig: TiVoLaunchAttributes = filterEmptyValues(uriParams) || {};
      if (attributesWithCriteria) {
        uriConfig = {
          ...uriConfig,
          attributesWithCriteria: JSON.parse(attributesWithCriteria),
        };
      }
      adapted[launchType] = uriConfig;
    }
  });

  return adapted;
}

function adaptZoomConfigurationsToRequest(
  zoomConfigurations: VariantForm["zoomConfigurations"]
): TiVoAppVariantVersion["zoomConfigurations"] | undefined {
  return zoomConfigurations?.map(
    ({ targets, zoomFactor, zoomFactorRatio }) => ({
      targets,
      zoomFactor: zoomFactor || null,
      zoomFactorRatio: zoomFactorRatio || null,
    })
  );
}

export function adaptVariantFormToRequest(
  form: VariantForm,
  countries?: Country[]
): Partial<TiVoAppVariantVersion> {
  const providerSpecificNameValueMap =
    form.uriTransform.providerSpecificNameValueMap.length > 0
      ? form.uriTransform.providerSpecificNameValueMap.reduce(
          (acc, { name, value }) => ({ ...acc, [name]: value }),
          {} as TiVoUriTransform["providerSpecificNameValueMap"]
        )
      : undefined;
  const uriTransform: TiVoAppVariantVersion["uriTransform"] = {
    ...form.uriTransform,
    alternateBasePlaybackUrl:
      form.uriTransform.alternateBasePlaybackUrl || undefined,
    className: form.uriTransform.className || undefined,
    providerSpecificNameValueMap,
  };
  return {
    adsEnabled: form.adsEnabled === "true",
    countries: adaptSupportedCountries(form, countries) || [],
    label: form.label,
    launchAttributes: adaptLaunchAttributes(
      form.launchAttributes,
      form.launchAttributesTabs
    ),
    targets: form.targets,
    uriTransform,
    zoomConfigurations: adaptZoomConfigurationsToRequest(
      form.zoomConfigurations
    ),
  };
}

const PROVIDER_PATCH_KEYS = [
  ...Object.values(PROVIDER_FIELDS.SUPPORT),
  ...Object.values(PROVIDER_FIELDS.CSP_SUMMARY),
].map(({ name }) => name) as (keyof TiVoAppProvider)[];

export function adaptProviderFormToPatch(
  form: ProviderForm
): Partial<TiVoAppProvider> {
  const picked = Object.fromEntries(
    PROVIDER_PATCH_KEYS.filter((key) => key in form).map((key) => [
      key,
      form[key as keyof ProviderForm],
    ])
  ) as Partial<TiVoAppProvider>;

  return filterEmptyValues(picked) || {};
}

// - - - - - - - - Presentation config utils
const DEFAULT_SORT_ORDER = 100000;

const PRESENTATION_MODE_VALUES = {
  default: {
    boolean: "off",
    recommendationsType: "userControlled",
    sortOrder: DEFAULT_SORT_ORDER,
    supportsEpisodeDeepLinks: "on",
  },
  inherited: {
    boolean: "inherited",
    recommendationsType: "inherited",
    sortOrder: "",
    supportsEpisodeDeepLinks: "inherited",
  },
} as const;

export function getInitialPresentationConfig(
  makeDefault = false
): TiVoPresentationConfigForm {
  const mode = PRESENTATION_MODE_VALUES[makeDefault ? "default" : "inherited"];
  return {
    matchByPlatformSupportedFeatures: false,
    attributes: {
      alwaysVisibleInMyApps: mode.boolean,
      appManifestAvailable: mode.boolean,
      browsePlaybackSortOrder: "",
      enabledByDefault: mode.boolean,
      inMyAppsByDefault: mode.boolean,
      myAppsRangeGroup: "",
      name: "",
      platformSupportedApps: [],
      promoted: mode.boolean,
      postPublishDaysConsideredNew: "",
      publishDateTime: "",
      recommendationsType: mode.recommendationsType,
      shouldShowInList: mode.boolean,
      shouldShowInSearchInApps: mode.boolean,
      sortOrder: mode.sortOrder,
      supportsEpisodeDeepLinks: mode.supportsEpisodeDeepLinks,
    },
    countries: makeDefault ? undefined : [],
    platformSupportedFeatures: makeDefault ? undefined : [],
  };
}

// - - - - - - - - Images utils

const convertBlobUrlToFile = async (
  blobUrl: string,
  filename: string = "image"
): Promise<File> => {
  const response = await fetch(blobUrl);
  const blob = await response.blob();
  return new File([blob], filename, { type: blob.type });
};

type ProcessedAsset = {
  blobUrl: string;
  file: File;
};

const downloadAndConvertImage = async (
  url: string
): Promise<ProcessedAsset> => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const filename = url.split("/").pop()?.split("?")[0] || "";
    const file = new File([blob], filename, { type: blob.type });
    const blobUrl = URL.createObjectURL(blob);

    return { file, blobUrl };
  } catch (error) {
    console.error("Failed to download image:", error);
    throw error;
  }
};

const prepareAssetForUpload = async (
  key: string,
  value: string = ""
): Promise<ProcessedAsset | null> => {
  if (value.startsWith("blob:")) {
    const file = await convertBlobUrlToFile(value, key);
    return { blobUrl: value, file };
  }
  if (value.startsWith("http://") || value.startsWith("https://")) {
    return await downloadAndConvertImage(value);
  }
  console.warn(`No processor found for asset: ${key}`, value);
  return null;
};

const uploadAsset = (
  key: string,
  asset: ProcessedAsset,
  setImage: (name: string, file: File, callback: (url: string) => void) => void,
  setFieldValue: (field: string, value: any) => void
): void => {
  const field = `assets.${IMAGE_ENVS.PROD}.${key}.url`;
  setImage(field, asset.file, (url) => {
    URL.revokeObjectURL(asset.blobUrl);
    setFieldValue(field, url); // after submit replace blob with url
  });
  setFieldValue(field, asset.blobUrl);
};

export const copyAssetsToProduction = async (
  stagingAssets: Record<string, { url: string }> = {},
  setImage: (name: string, file: File, callback: (url: string) => void) => void,
  setFieldValue: (field: string, value: any) => void
): Promise<void> => {
  for (const [key, value] of Object.entries(stagingAssets)) {
    try {
      const assetsForUpload = await prepareAssetForUpload(key, value.url);
      if (assetsForUpload) {
        uploadAsset(key, assetsForUpload, setImage, setFieldValue);
      }
    } catch (error) {
      console.error(`Failed to process asset: ${key}`, error);
      throw `Failed to process asset: ${key}`;
    }
  }
};

// - - - - - - - - Other utils

export function useFormMode(): FormMode {
  const { pathname } = useLocation();
  const parts = pathname.replace(/^\/+|\/+$/g, "").split("/");

  const [, action] = parts; // "create" or "edit"
  const isVariant = parts.includes("variant");

  if (action === "create") {
    return isVariant ? "createVariant" : "createProvider";
  }
  if (action === "edit") {
    return isVariant ? "editVariant" : "editProvider";
  }
  console.error("Mode not supported");
  throw new Error("Mode not supported");
}

function filterEmptyValues<T>(obj: T): T {
  const isNonEmpty = (value: any): boolean => {
    if (typeof value === "number" || typeof value === "boolean") {
      return true;
    }
    if (value === null) {
      return false;
    }
    if (Array.isArray(value) || typeof value === "object") {
      return Object.keys(value).length > 0;
    }
    return Boolean(value);
  };

  if (Array.isArray(obj)) {
    return obj.map(filterEmptyValues).filter(isNonEmpty) as T;
  } else if (typeof obj === "object" && obj !== null) {
    return Object.fromEntries(
      Object.entries(obj)
        .map(([key, value]) => [key, filterEmptyValues(value)])
        .filter(([, value]) => isNonEmpty(value))
    ) as T;
  }
  return obj;
}
